import { useState, useEffect, useRef, useContext } from "react";
import { useAppAuth } from "../MockAuthProvider";
import { record } from "aws-amplify/analytics";
import { ChatContext } from "../context/ChatContext";
import { currentConfig, LOCAL_DEV_MODE } from "../config";
import { useStagedLoadingMessage } from "./useStagedLoadingMessage";

// The API returns the whole answer in one JSON body: it is an API Gateway REST
// endpoint in front of a Lambda that consumes the Bedrock agent stream
// server-side, and neither can stream to the browser. So the response is paced
// out here instead. Roughly REVEAL_TICKS updates are spread across the text,
// which keeps the reveal about the same duration whether the answer is three
// lines or three pages.
const REVEAL_TICK_MS = 25;
const REVEAL_TICKS = 60;

export const useChatCoachAI = () => {
  const auth = useAppAuth();
  
  const { messages, setMessages, sessionId } = useContext(ChatContext);
  const [userInput, setUserInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const loadingMessage = useStagedLoadingMessage(loading, streaming);
  const scrollRef = useRef(null);
  const sessionStartTimeRef = useRef(Date.now());
  const revealTimerRef = useRef(null);
  const pendingRevealRef = useRef(null);
  // A request is awaiting its response. A ref rather than `loading` so two
  // sends in the same tick (double Enter) can't both get through.
  const requestInFlightRef = useRef(false);

  // Never leave a reveal timer running after this hook goes away.
  useEffect(() => () => clearInterval(revealTimerRef.current), []);

  /**
   * Stop any reveal in progress and commit the full response.
   *
   * Returns the authoritative message list so the caller can build the next
   * turn on it directly: `messages` from context is still the pre-commit value
   * on this tick, so reading it here would drop the response.
   */
  const commitReveal = () => {
    if (revealTimerRef.current) {
      clearInterval(revealTimerRef.current);
      revealTimerRef.current = null;
    }

    const pending = pendingRevealRef.current;
    if (!pending) return messages;

    pendingRevealRef.current = null;
    const committed = [
      ...pending.base,
      { role: pending.role, content: pending.text },
    ];
    setMessages(committed);
    setStreaming(false);
    return committed;
  };

  /** Pace the response out word by word, so the streaming cursor is real. */
  const revealResponse = (base, text, role = "assistant") => {
    pendingRevealRef.current = { base, text, role };
    const step = Math.max(1, Math.ceil(text.length / REVEAL_TICKS));
    let shown = 0;

    // The first token has landed: the wait is over, so the staged loading
    // text clears and the cursor takes over.
    setLoading(false);
    setStreaming(true);
    setMessages([...base, { role, content: "" }]);

    revealTimerRef.current = setInterval(() => {
      shown = Math.min(shown + step, text.length);
      // Snap to a word boundary so markdown tokens aren't rendered half-built
      // (a bare "**bol" would flash as literal asterisks).
      if (shown < text.length) {
        const boundary = text.indexOf(" ", shown);
        shown = boundary === -1 ? text.length : boundary;
      }
      setMessages([...base, { role, content: text.slice(0, shown) }]);

      if (shown >= text.length) {
        clearInterval(revealTimerRef.current);
        revealTimerRef.current = null;
        pendingRevealRef.current = null;
        setStreaming(false);
      }
    }, REVEAL_TICK_MS);
  };

  // Track Unique View and Session Duration (skip in local dev)
  useEffect(() => {
    if (LOCAL_DEV_MODE) {
      console.log('🔧 [LOCAL DEV MODE] Chat analytics disabled for local testing');
      return;
    }
    
    record({
      name: "page_view",
      attributes: { userEmail: auth.user?.profile?.email, page: "ChatCoachAI" },
    });
    sessionStartTimeRef.current = Date.now();
    record({ name: "session_start" });

    const recordSessionEnd = () => {
      const sessionEndTime = Date.now();
      const durationInSeconds = Math.round(
        (sessionEndTime - sessionStartTimeRef.current) / 1000
      );
      record({
        name: "session_end",
        attributes: { duration: durationInSeconds.toString() },
        metrics: { sessionDuration: durationInSeconds },
      });
    };

    const handleBeforeUnload = () => recordSessionEnd();
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      recordSessionEnd();
    };
  }, []);

  /**
   * Send message function with conditional authentication.
   *
   * @param {string|null} question text to send instead of the input box
   * @param {{promptPrefix?: string}} options `promptPrefix` is prepended to the
   *   text for the model and for the conversation history, but not for display.
   *   A pasted transcript therefore stays readable in the chat while the model
   *   still receives its review instructions.
   */
  const handleSend = async (question = null, options = {}) => {
    const inputText = question || userInput;
    if (!inputText.trim()) return;

    // Drop sends while a request is awaiting its response. Each request holds
    // the message list it was built from, so letting a second turn start would
    // let the first response resolve and overwrite it. Sending during a
    // *reveal* is fine: commitReveal below folds it in first.
    if (requestInFlightRef.current) return;

    const { promptPrefix } = options;

    // Sending during a reveal finishes it first, so the new turn builds on the
    // complete previous answer rather than a half-revealed one.
    const baseMessages = commitReveal();

    // Record a chat message event (skip in local dev)
    if (!LOCAL_DEV_MODE) {
      record({
        name: "chat_message_sent",
        attributes: {
          page: "ChatCoachAI",
          agentType: "roleplay",
        },
      });
    } else {
      console.log('🔧 [LOCAL DEV MODE] Chat message analytics disabled');
    }

    // Authentication handling - match original working code
    if (!auth || !auth.isAuthenticated) {
      console.error("User is not authenticated");
      return;
    }

    const token = auth.user?.id_token; // Use id_token like original
    if (!token) {
      console.error("Missing access token");
      setMessages([
        ...baseMessages,
        {
          role: "assistant",
          content: "Authentication token is missing. Please sign in again.",
        },
      ]);
      return;
    }


    setUserInput("");
    setLoading(true);
    requestInFlightRef.current = true;

    // Append the new user message to the current messages. `content` is what
    // the chat shows; `promptContent`, when present, is what the model sees.
    const userMessage = { role: "user", content: inputText };
    if (promptPrefix) {
      userMessage.promptContent = `${promptPrefix}\n${inputText}`;
    }
    const newMessages = [...baseMessages, userMessage];
    setMessages(newMessages);

    // Build a prompt that includes the conversation history
    const conversationHistory = newMessages
      .map(
        (msg) =>
          `${msg.role === "user" ? "User" : "CoachAI"}: ${
            msg.promptContent || msg.content
          }`
      )
      .join("\n");

    try {
      const apiUrl = currentConfig.apiUrl;
      const response = await fetch(`${apiUrl}/coachai`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`, // Always send Bearer token like original
        },
        body: JSON.stringify({
          prompt: conversationHistory,
          sessionId: sessionId || "",
          stream: true,
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.error("401 Unauthorized - API authentication issue");
        }
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data && data.text) {
        revealResponse(newMessages, data.text);
      } else {
        setMessages([...newMessages, { role: "assistant", content: "No response received." }]);
      }
    } catch (error) {
      console.error("Error calling AI:", error);
      setMessages([
        ...newMessages,
        { role: "assistant", content: "Error retrieving AI response." },
      ]);
    } finally {
      requestInFlightRef.current = false;
      // A reveal in progress owns these flags until it finishes; clearing them
      // here would cut the response off mid-word.
      if (!revealTimerRef.current) {
        setLoading(false);
        setStreaming(false);
      }
    }
  };

  return {
    userInput,
    setUserInput,
    loading,
    streaming,
    loadingMessage,
    handleSend,
    scrollRef,
  };
};
